"use client";
// beui.dev/components/motion/popover
// Bavimail's one popover (the gooey `Popover` in popover.tsx is not used).
// Restyled for Bavimail (DESIGN.md 4 Elevation, BRAND_SPEC "Floating panels"):
// sharp dashed panel on `surface` that carries its own `font-hb text-hb-text`
// (it is portalled out of `.hb-shell`), `shadow-xl shadow-black/50`, `radius`
// default 6px (was 16).
// Entrance: beUI's corner clip-morph is replaced by the quiet fade + slight
// scale `modal.tsx` uses, anchored to the trigger-side corner; exit is a 0.16s
// fade. With the clip gone the panel no longer needs `overflow-hidden`, so
// absolutely positioned children (an inline list that opens past the panel
// edge) are not cut off.
// Bavimail additions:
// - Collision handling: the panel flips to the other side when it cannot fit and
//   is clamped inside the viewport (`collisionPadding`, default 8).
// - Focus: moves into the panel on open (`autoFocus`, default true), Tab cycles
//   inside it, and focus returns to the trigger on close.
// - Layering: `z-[9999]` (above the z-50 Drawer, the z-[100] Modal and equal to
//   the combobox list, which mounts later and so paints on top). Escape is
//   handled by the topmost floating layer only and never reaches a Drawer /
//   Modal beneath (`useLayeredEscape`).
// - Outside press: anything rendered inside the panel counts as inside, however
//   far it was portalled (React-tree check). Floating content owned by another
//   React root is matched by `[data-popover-ignore]`, `[data-floating]`,
//   `[data-combobox-content]`, plus the `ignoreSelector` prop on `MorphPopover`.
//   Only the trigger counts as inside, not the whole root wrapper (upstream
//   counted the wrapper, which kept the panel open when a sibling was pressed).
// - `MorphPopoverContent` `ariaLabel`; `MorphPopoverTrigger` honours a child
//   `onClick` that calls `preventDefault()` and sets `data-state`.

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  cloneElement,
  createContext,
  isValidElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { usePopoverPortalPosition } from "@/components/motion/popover-position";
import { EASE_OUT, SPRING_PANEL } from "@/lib/ease";
import {
  FLOATING_IGNORE_SELECTOR,
  isInsideFloating,
  useLayeredEscape,
  useOutsidePointer,
} from "@/lib/hooks/use-dismiss";
import { PresenceGate } from "@/lib/presence-gate";
import { cn } from "@/lib/utils";

type Side = "top" | "bottom";
type Align = "start" | "end";

type MorphContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  close: () => void;
  triggerId: string;
  contentId: string;
  /** The element the panel measures against — see `registerTrigger`. */
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  registerTrigger: (node: HTMLElement | null) => void;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
  layerProps: { onPointerDownCapture: () => void };
};

const MorphContext = createContext<MorphContextValue | null>(null);

function useMorphContext(component: string) {
  const ctx = useContext(MorphContext);
  if (!ctx) throw new Error(`${component} must be used within <MorphPopover>`);
  return ctx;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getFocusableElements(root: HTMLElement | null) {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.tabIndex >= 0 && !element.closest("[inert]"),
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

// Fade + slight scale, the entrance `modal.tsx` uses. The exit is a short fade.
const PANEL_HIDDEN = { opacity: 0, scale: 0.98 } as const;
const PANEL_VISIBLE = { opacity: 1, scale: 1 } as const;
const PANEL_EXIT_TRANSITION = { duration: 0.16, ease: EASE_OUT } as const;

export interface MorphPopoverProps {
  children: ReactNode;
  /** Controlled open state. */
  open?: boolean;
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Bavimail addition — extra CSS selector for floating content that belongs
   * to this popover but is rendered outside both its DOM and its React tree (a
   * third-party picker mounted by another root). A press inside a match does
   * not dismiss, and Escape goes to a visibly open match first. Added to the
   * defaults `[data-popover-ignore], [data-floating], [data-combobox-content]`.
   * Content rendered by the panel's own children never needs this, portalled or
   * not.
   */
  ignoreSelector?: string;
  className?: string;
}

/**
 * A popover anchored to its trigger that fades and scales in from the
 * trigger-side corner. Closes on an outside press or Escape. Controlled or
 * uncontrolled.
 */
export function MorphPopover({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  ignoreSelector,
  className,
}: MorphPopoverProps) {
  const baseId = useId();
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : internalOpen;
  const floatingSelector = ignoreSelector
    ? `${FLOATING_IGNORE_SELECTOR}, ${ignoreSelector}`
    : FLOATING_IGNORE_SELECTOR;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );
  const toggle = useCallback(() => setOpen(!open), [setOpen, open]);

  // A trigger normally registers itself through MorphPopoverTrigger. It can't
  // when something else already clones the element — a Tooltip wrapping the
  // button, say — and an unregistered trigger leaves the panel with nothing to
  // measure against, so it renders permanently invisible. The root boxes the
  // trigger exactly (the content portals out of it), so it stands in until a
  // real trigger registers, and stands in again if that one unmounts. Both are
  // state, so a trigger arriving while the panel is open re-anchors it.
  const anchor = trigger ?? root;
  const anchorRef = useMemo<React.MutableRefObject<HTMLElement | null>>(
    () => ({ current: anchor }),
    [anchor],
  );

  // The panel is a `role="dialog"` and unmounts when it closes, so focus cannot
  // be left sitting inside it: a dismissal hands it back to the trigger, the
  // way the ARIA dialog pattern asks. A pointer dismissal takes the focus
  // onward itself when it lands on something focusable — this only catches the
  // case where it would otherwise be stranded. When no trigger has registered,
  // the root anchor stands in only if it can actually hold focus.
  const close = useCallback(() => {
    const focused = document.activeElement;
    const stranded =
      !focused || focused === document.body || Boolean(contentRef.current?.contains(focused));
    setOpen(false);
    if (!stranded) return;
    const restore = trigger ?? (root && root.tabIndex >= 0 ? root : null);
    restore?.focus({ preventScroll: true });
  }, [root, setOpen, trigger]);

  // The trigger toggles on its own click, so the press that starts that click
  // is not an outside press. Siblings of the trigger inside the root are.
  const isInside = useCallback(
    (target: Element) =>
      Boolean(contentRef.current?.contains(target)) ||
      Boolean(anchor?.contains(target)) ||
      isInsideFloating(target, floatingSelector),
    [anchor, floatingSelector],
  );
  const layerProps = useOutsidePointer(open, close, isInside);
  useLayeredEscape(open, close, {
    nestedSelector: floatingSelector,
    self: contentRef,
  });

  const ctx = useMemo<MorphContextValue>(
    () => ({
      open,
      setOpen,
      toggle,
      close,
      triggerId: `${baseId}-trigger`,
      contentId: `${baseId}-content`,
      triggerRef: anchorRef,
      registerTrigger: setTrigger,
      contentRef,
      layerProps,
    }),
    [open, setOpen, toggle, close, baseId, anchorRef, layerProps],
  );

  return (
    <MorphContext.Provider value={ctx}>
      <div ref={setRoot} className={cn("relative inline-flex", className)}>
        {children}
      </div>
    </MorphContext.Provider>
  );
}

export interface MorphPopoverTriggerProps {
  /** A single focusable element (e.g. a `Button`). Its own props and ref are kept. */
  children: ReactElement;
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(node);
      else if (ref && typeof ref === "object")
        (ref as React.MutableRefObject<T | null>).current = node;
    }
  };
}

/**
 * Wraps a single element, toggling the popover on click (so also on Enter and
 * Space). The child's `onClick` runs first and can `preventDefault()` to veto.
 */
export function MorphPopoverTrigger({ children }: MorphPopoverTriggerProps) {
  const ctx = useMorphContext("MorphPopoverTrigger");
  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const childOnClick = child.props.onClick as
    | ((e: ReactMouseEvent<HTMLElement>) => void)
    | undefined;
  const childRef = (child.props as { ref?: Ref<HTMLElement> }).ref;

  return cloneElement(child, {
    id: ctx.triggerId,
    ref: mergeRefs(childRef, ctx.registerTrigger),
    onClick: (e: ReactMouseEvent<HTMLElement>) => {
      childOnClick?.(e);
      if (e.defaultPrevented) return;
      if (ctx.open) ctx.close();
      else ctx.toggle();
    },
    "aria-haspopup": "dialog",
    "aria-expanded": ctx.open,
    "aria-controls": ctx.open ? ctx.contentId : undefined,
    "data-state": ctx.open ? "open" : "closed",
  });
}

export interface MorphPopoverContentProps {
  children: ReactNode;
  /** Preferred side. Flips when the panel cannot fit there. Default "bottom". */
  side?: Side;
  /** Which trigger edge the panel lines up with. Default "end". */
  align?: Align;
  /** Gap between trigger and panel, in px. Default 8. */
  sideOffset?: number;
  /** Panel corner radius, in px. Default 6 (Bavimail panels are sharp). */
  radius?: number;
  /** Bavimail addition — minimum distance kept from the viewport edges, in px. Default 8. */
  collisionPadding?: number;
  /** Bavimail addition — move focus into the panel when it opens. Default true. */
  autoFocus?: boolean;
  /** Bavimail addition — accessible name. Defaults to the trigger's own label. */
  ariaLabel?: string;
  className?: string;
}

export function MorphPopoverContent({
  children,
  side = "bottom",
  align = "end",
  sideOffset = 8,
  radius = 6,
  collisionPadding = 8,
  autoFocus = true,
  ariaLabel,
  className,
}: MorphPopoverContentProps) {
  const ctx = useMorphContext("MorphPopoverContent");
  const reduce = useReducedMotion() ?? false;
  const [portalReady, setPortalReady] = useState(false);
  const layout = usePopoverPortalPosition(ctx.triggerRef, ctx.contentRef, portalReady && ctx.open);

  useEffect(() => setPortalReady(true), []);

  useEffect(() => {
    if (!ctx.open || !portalReady || !autoFocus) return;
    const frame = requestAnimationFrame(() => {
      const panel = ctx.contentRef.current;
      // A panel that focused its own field on mount keeps it.
      if (!panel || panel.contains(document.activeElement)) return;
      const [firstFocusable] = getFocusableElements(panel);
      (firstFocusable ?? panel).focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [ctx.open, ctx.contentRef, portalReady, autoFocus]);

  // Tab cycles inside the panel: it is portalled to the end of <body>, so
  // walking out of it would land nowhere near the trigger. Focus that sits in
  // nested portalled content (a combobox list) is left to that content.
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab" || event.defaultPrevented) return;
    const panel = ctx.contentRef.current;
    const focused = document.activeElement;
    if (!panel || !(focused instanceof HTMLElement) || !panel.contains(focused)) return;
    const focusable = getFocusableElements(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (focused === first || focused === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && focused === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Keep the server and first client render identical, then mount the portal.
  if (!portalReady) return null;

  let left = 0;
  let top = 0;
  let originX = 0;
  let originY = 0;
  if (layout) {
    const { trigger, content } = layout;
    const below = trigger.top + trigger.height + sideOffset;
    const above = trigger.top - content.height - sideOffset;
    const fitsBelow = below + content.height <= window.innerHeight - collisionPadding;
    const fitsAbove = above >= collisionPadding;
    const roomBelow = window.innerHeight - (trigger.top + trigger.height);
    const roomAbove = trigger.top;
    let resolved = side;
    if (side === "bottom" && !fitsBelow && (fitsAbove || roomAbove > roomBelow)) {
      resolved = "top";
    } else if (side === "top" && !fitsAbove && (fitsBelow || roomBelow > roomAbove)) {
      resolved = "bottom";
    }

    const anchorX = align === "end" ? trigger.left + trigger.width : trigger.left;
    left = clamp(
      align === "end" ? anchorX - content.width : anchorX,
      collisionPadding,
      Math.max(collisionPadding, window.innerWidth - content.width - collisionPadding),
    );
    top = clamp(
      resolved === "bottom" ? below : above,
      collisionPadding,
      Math.max(collisionPadding, window.innerHeight - content.height - collisionPadding),
    );
    // The corner nearest the trigger, in panel coordinates.
    originX = clamp(anchorX - left, 0, content.width);
    originY = resolved === "bottom" ? 0 : content.height;
  }

  return createPortal(
    <AnimatePresence>
      {ctx.open ? (
        <PresenceGate>
          {({ gate }) => (
            <motion.div
              data-morph-popover-portal=""
              initial={reduce ? { opacity: 0 } : PANEL_HIDDEN}
              animate={PANEL_VISIBLE}
              exit={{
                ...(reduce ? { opacity: 0 } : PANEL_HIDDEN),
                transition: PANEL_EXIT_TRANSITION,
              }}
              transition={reduce ? { duration: 0.12, ease: EASE_OUT } : SPRING_PANEL}
              {...gate}
              {...ctx.layerProps}
              style={{
                ...gate.style,
                left,
                top,
                visibility: layout ? "visible" : "hidden",
                transformOrigin: `${originX}px ${originY}px`,
              }}
              className="fixed z-[9999]"
            >
              <div
                ref={ctx.contentRef}
                id={ctx.contentId}
                role="dialog"
                aria-label={ariaLabel}
                aria-labelledby={ariaLabel ? undefined : ctx.triggerId}
                tabIndex={-1}
                onKeyDown={onKeyDown}
                style={{ borderRadius: radius }}
                className={cn(
                  "hb-shell border border-dashed border-hb-card bg-hb-surface font-hb text-hb-text shadow-xl shadow-black/50 outline-none",
                  className,
                )}
              >
                {children}
              </div>
            </motion.div>
          )}
        </PresenceGate>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
